// ApiCredentials.tsx
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Box,
} from '@mui/material';
import {
  Key as KeyIcon,
  Users as GroupsIcon,
  Info as InfoIcon,
} from 'lucide-mui';
import { getToken } from '../../../lib/localStorageHandler';
import { RootState } from '../../../reducers';
import { ToggleSetting, CopyButton } from '../common';
import ContactSalesDialog from '../../sales/ContactSalesDialog';

const SNACKBAR_DURATION = 4000;

const ApiCredentials = () => {
  const token = getToken() || '';
  const { team } = useSelector((state: RootState) => state);
  const { teamId = '' } = team.selectedTeam || {};

  const [formState, setFormState] = useState({
    apiLearnMoreOpen: false,
    salesDialogOpen: false,
  });

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
  });

  const handleClose = () => {
    setFormState((prev) => ({ ...prev, apiLearnMoreOpen: false }));
  };

  const handleSalesDialogClose = () => {
    setFormState((prev) => ({ ...prev, salesDialogOpen: false }));
  };

  const handleCopy = (label: string) => {
    setSnackbarState({
      open: true,
      message: `${label} copied to clipboard`,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarState({ open: false, message: '' });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <ToggleSetting
        checked={false}
        onChange={() => {}}
        label="Learn More"
        description="Understand how to use your API credentials"
        icon={<InfoIcon />}
        rightElement={
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() =>
              setFormState((prev) => ({ ...prev, apiLearnMoreOpen: true }))
            }
          >
            Learn More
          </Button>
        }
      />

      <ToggleSetting
        checked={false}
        onChange={() => {}}
        label="Token"
        description="Your API authentication token"
        icon={<KeyIcon />}
      >
        <Box sx={{ position: 'relative' }}>
          <TextField
            value={token}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '4px',
              right: '4px',
            }}
          >
            <CopyButton text={token} onCopy={() => handleCopy('Token')} />
          </Box>
        </Box>
      </ToggleSetting>

      <ToggleSetting
        checked={false}
        onChange={() => {}}
        label="Team ID"
        description="Your unique team identifier"
        icon={<GroupsIcon />}
      >
        <Box sx={{ position: 'relative' }}>
          <TextField
            value={teamId}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '4px',
              right: '4px',
            }}
          >
            <CopyButton text={teamId} onCopy={() => handleCopy('Team ID')} />
          </Box>
        </Box>
      </ToggleSetting>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
        message={snackbarState.message}
      />

      {/* Dialog for learning more about API Credentials */}
      <Dialog
        open={formState.apiLearnMoreOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Understanding API Credentials</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography paragraph>
              API credentials allow your applications to securely communicate
              with our service.
            </Typography>

            <Typography paragraph>
              The Token is like a special password that proves your application
              has permission to use our service. Keep it secret and never share
              it publicly.
            </Typography>

            <Typography paragraph>
              The Team ID is your unique identifier in our system. You&apos;ll
              need both the Token and Team ID when making API requests.
            </Typography>

            <Typography paragraph>
              These credentials are essential if you want to integrate our
              service into your own applications or automate certain tasks.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                onClick={() =>
                  setFormState((prev) => ({ ...prev, salesDialogOpen: true }))
                }
                variant="outlined"
                color="secondary"
                sx={{ textTransform: 'none' }}
              >
                Interested in integrating? Enquire here
              </Button>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {formState.salesDialogOpen && (
        <ContactSalesDialog
          onClose={handleSalesDialogClose}
          formSource="API Integration - Learn More"
        />
      )}
    </Box>
  );
};

export default ApiCredentials;
